// Role-based permissions configuration

export const ROLES = {
  ADMINISTRATOR: 'administrator',
  MANAGER: 'manager',
  DRIVER: 'driver',
  ASSISTANT: 'assistant',
  PARENT: 'parent'
};

// Define which modules each role can access
export const ROLE_PERMISSIONS = {
  [ROLES.ADMINISTRATOR]: {
    // Administrators have access to everything
    modules: [
      'dashboard',
      'fleet',
      'track',
      'personnel',
      'students',
      'parents',
      'roster',
      'routes',
      'analytics',
      'ai-detection',
      'devices',
      'billing',
      'users',
      'messages'
    ],
    canAddVehicle: true,
    canEditVehicle: true,
    canDeleteVehicle: true,
    canAddDevice: true
  },
  [ROLES.MANAGER]: {
    // Managers have access to all modules but cannot add vehicles or devices
    modules: [
      'dashboard',
      'fleet',
      'track',
      'personnel',
      'students',
      'parents',
      'roster',
      'routes',
      'analytics',
      'ai-detection',
      'devices',
      'billing',
      'users',
      'messages'
    ],
    canAddVehicle: false, // Managers cannot add vehicles
    canEditVehicle: true,
    canDeleteVehicle: true,
    canAddDevice: false // Managers cannot add devices
  },
  [ROLES.DRIVER]: {
    // Drivers can only access: Fleet, Students, Parents, Optimized Routes, and Roster
    modules: [
      'dashboard',
      'fleet',
      'students',
      'parents',
      'roster',
      'routes' // Only optimized routes, not engine
    ],
    canAddVehicle: false,
    canEditVehicle: false,
    canDeleteVehicle: false,
    canAddDevice: false
  },
  [ROLES.ASSISTANT]: {
    // Assistants have same access as drivers
    modules: [
      'dashboard',
      'fleet',
      'students',
      'parents',
      'roster',
      'routes' // Only optimized routes, not engine
    ],
    canAddVehicle: false,
    canEditVehicle: false,
    canDeleteVehicle: false,
    canAddDevice: false
  },
  [ROLES.PARENT]: {
    // Parents need dashboard and messaging access
    modules: [
      'dashboard',
      'messages'
    ],
    canAddVehicle: false,
    canEditVehicle: false,
    canDeleteVehicle: false,
    canAddDevice: false
  }
};

// Check if a role has access to a module
export const hasModuleAccess = (role, module) => {
  if (!role || !ROLE_PERMISSIONS[role]) {
    return false;
  }
  
  // Normalize module name (remove leading slash, handle routes specially)
  const normalizedModule = module.replace(/^\//, '');
  
  // Handle routes submodules
  if (normalizedModule === 'routes/engine') {
    // Only admin and manager can access routes engine
    return role === ROLES.ADMINISTRATOR || role === ROLES.MANAGER;
  }
  
  if (normalizedModule === 'routes/optimized' || normalizedModule === 'routes') {
    return ROLE_PERMISSIONS[role].modules.includes('routes');
  }
  
  return ROLE_PERMISSIONS[role].modules.includes(normalizedModule);
};

// Check if a role can add vehicles
export const canAddVehicle = (role) => {
  if (!role || !ROLE_PERMISSIONS[role]) {
    return false;
  }
  return ROLE_PERMISSIONS[role].canAddVehicle;
};

// Check if a role can edit vehicles
export const canEditVehicle = (role) => {
  if (!role || !ROLE_PERMISSIONS[role]) {
    return false;
  }
  return ROLE_PERMISSIONS[role].canEditVehicle;
};

// Check if a role can delete vehicles
export const canDeleteVehicle = (role) => {
  if (!role || !ROLE_PERMISSIONS[role]) {
    return false;
  }
  return ROLE_PERMISSIONS[role].canDeleteVehicle;
};

// Check if a role can add devices
export const canAddDevice = (role) => {
  if (!role || !ROLE_PERMISSIONS[role]) {
    return false;
  }
  return ROLE_PERMISSIONS[role].canAddDevice;
};

// Get all accessible modules for a role
export const getAccessibleModules = (role) => {
  if (!role || !ROLE_PERMISSIONS[role]) {
    return [];
  }
  return ROLE_PERMISSIONS[role].modules;
};

